import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePatientDetail } from '../hooks/usePatientDetail';
import { usePatientCatalogs } from '../hooks/usePatientCatalogs';
import Spinner from '../../../components/ui/Spinner';
import DatePickerInput from '../../../components/ui/DatePickerInput';
import ConfirmModal from '../../../components/common/ConfirmModal';

const PatientEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const patientId = id ? parseInt(id) : undefined;

  const {
    formData,
    errors,
    loading: initialLoading,
    saving,
    handleSubmit,
    handleInputChange,
    resetForm,
  } = usePatientDetail(patientId);

  const {
    identificationTypes,
    nationalities,
    provinces,
    municipalities,
    districts,
    insurancePlans,
    loading: catalogsLoading,
    getMunicipalitiesByProvince,
    getDistrictsByMunicipality,
  } = usePatientCatalogs();

  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const filteredMunicipalities = formData.provinceId
    ? getMunicipalitiesByProvince(formData.provinceId)
    : [];

  const filteredDistricts = selectedMunicipalityId
    ? getDistrictsByMunicipality(selectedMunicipalityId)
    : [];

  // selectedMunicipalityId only ever gets set by the Provincia/Municipio
  // onChange handlers below, so on a fresh edit load it stays null even
  // when the patient already has a saved location — leaving Municipio
  // blank and Distrito disabled despite formData.municipalDistrictId
  // holding the real value. Derive it once from the loaded district as
  // soon as the catalogs are ready, without overriding later manual picks.
  const didInitMunicipalityRef = useRef(false);
  useEffect(() => {
    if (didInitMunicipalityRef.current) return;
    // Wait for both the patient record and the catalogs to finish loading —
    // locking the ref on districts alone races against the patient fetch:
    // if catalogs resolve first, formData.municipalDistrictId is still null
    // when the ref locks, and the derivation never gets a second chance once
    // the real value arrives.
    if (initialLoading || catalogsLoading) return;
    if (districts.length === 0) return;
    didInitMunicipalityRef.current = true;
    if (!formData.municipalDistrictId) return;
    const district = districts.find(d => d.id === formData.municipalDistrictId);
    if (district) {
      setSelectedMunicipalityId(district.municipality_id);
    }
  }, [initialLoading, catalogsLoading, formData.municipalDistrictId, districts]);

  if (initialLoading || catalogsLoading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <Spinner variant="full-page" text="Cargando..." size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fa fa-user"></i> Editar Paciente
                {(formData.firstName || formData.lastName) && (
                  <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.8em', marginLeft: 10 }}>
                    — {formData.firstName} {formData.lastName}
                    {formData.recordNumber && ` (Exp. ${formData.recordNumber})`}
                  </span>
                )}
              </h3>
              <div className="card-tools">
                <Link to="/patients" className="btn btn-outline-secondary btn-sm">
                  <i className="fa fa-arrow-left"></i> Volver
                </Link>
              </div>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <fieldset>
                  <legend>Datos Personales</legend>
                </fieldset>

                {/* First Name and Last Name — shown first so it's immediately clear which patient is being edited */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">NOMBRE</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={saving}
                    />
                    {errors.firstName && <span className="text-danger">{errors.firstName}</span>}
                  </div>
                  <label className="col-sm-2 col-form-label">APELLIDO</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={saving}
                    />
                    {errors.lastName && <span className="text-danger">{errors.lastName}</span>}
                  </div>
                </div>

                {/* Record Number and Active */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">EXPEDIENTE</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.recordNumber ? 'is-invalid' : ''}`}
                      value={formData.recordNumber}
                      onChange={(e) => handleInputChange('recordNumber', e.target.value)}
                      disabled={saving}
                    />
                    {errors.recordNumber && <span className="text-danger">{errors.recordNumber}</span>}
                  </div>
                  <label className="col-sm-2 col-form-label">ACTIVO</label>
                  <div className="col-sm-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Identification Type and Number */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">TIPO ID</label>
                  <div className="col-sm-4">
                    <select
                      className="form-control"
                      value={formData.identificationType}
                      onChange={(e) => handleInputChange('identificationType', parseInt(e.target.value))}
                      disabled={saving}
                    >
                      {identificationTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="col-sm-2 col-form-label">IDENTIFICACIÓN</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className={`form-control ${errors.identification ? 'is-invalid' : ''}`}
                      value={formData.identification}
                      onChange={(e) => handleInputChange('identification', e.target.value)}
                      disabled={saving}
                    />
                    {errors.identification && <span className="text-danger">{errors.identification}</span>}
                  </div>
                </div>

                {/* Gender and Birth Date */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">GÉNERO</label>
                  <div className="col-sm-4" style={{ marginTop: '5px' }}>
                    <div className="form-check form-check-inline">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="genderMale"
                        value="true"
                        checked={formData.gender}
                        onChange={() => handleInputChange('gender', true)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="genderMale">Masculino</label>
                    </div>
                    <div className="form-check form-check-inline" style={{ marginLeft: '15px' }}>
                      <input
                        type="radio"
                        className="form-check-input"
                        id="genderFemale"
                        value="false"
                        checked={!formData.gender}
                        onChange={() => handleInputChange('gender', false)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="genderFemale">Femenino</label>
                    </div>
                  </div>
                  <label className="col-sm-2 col-form-label">FECHA NAC.</label>
                  <div className="col-sm-4">
                    <DatePickerInput
                      className={`form-control ${errors.birthDate ? 'is-invalid' : ''}`}
                      value={formData.birthDate}
                      onChange={(value) => handleInputChange('birthDate', value)}
                      disabled={saving}
                    />
                    {errors.birthDate && <span className="text-danger">{errors.birthDate}</span>}
                  </div>
                </div>

                {/* Nationality */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">NACIONALIDAD</label>
                  <div className="col-sm-4">
                    <select
                      className={`form-control ${errors.nationalityId ? 'is-invalid' : ''}`}
                      value={formData.nationalityId}
                      onChange={(e) => handleInputChange('nationalityId', parseInt(e.target.value))}
                      disabled={saving}
                    >
                      <option value={0}>Seleccionar</option>
                      {nationalities.map((nat) => (
                        <option key={nat.id} value={nat.id}>
                          {nat.name}
                        </option>
                      ))}
                    </select>
                    {errors.nationalityId && <span className="text-danger">{errors.nationalityId}</span>}
                  </div>
                </div>

                <fieldset>
                  <legend>Ubicación</legend>
                </fieldset>

                {/* Province, Municipality, District */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">PROVINCIA</label>
                  <div className="col-sm-4">
                    <select
                      className="form-control"
                      value={formData.provinceId || ''}
                      onChange={(e) => {
                        const provId = parseInt(e.target.value);
                        handleInputChange('provinceId', provId);
                        handleInputChange('municipalDistrictId', null);
                        setSelectedMunicipalityId(null);
                      }}
                      disabled={saving}
                    >
                      <option value="">Seleccionar</option>
                      {provinces.map((prov) => (
                        <option key={prov.id} value={prov.id}>
                          {prov.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="col-sm-2 col-form-label">MUNICIPIO</label>
                  <div className="col-sm-4">
                    <select
                      className="form-control"
                      value={selectedMunicipalityId || ''}
                      onChange={(e) => {
                        const munId = parseInt(e.target.value);
                        setSelectedMunicipalityId(munId);
                      }}
                      disabled={saving || !formData.provinceId}
                    >
                      <option value="">Seleccionar</option>
                      {filteredMunicipalities.map((mun) => (
                        <option key={mun.id} value={mun.id}>
                          {mun.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-2 col-form-label">DISTRITO MUNICIPAL</label>
                  <div className="col-sm-4">
                    <select
                      className="form-control"
                      value={formData.municipalDistrictId || ''}
                      onChange={(e) => handleInputChange('municipalDistrictId', parseInt(e.target.value))}
                      disabled={saving || !selectedMunicipalityId}
                    >
                      <option value="">Seleccionar</option>
                      {filteredDistricts.map((dist) => (
                        <option key={dist.id} value={dist.id}>
                          {dist.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">DIRECCIÓN</label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={saving}
                    />
                    {errors.address && <span className="text-danger">{errors.address}</span>}
                  </div>
                </div>

                {/* Contact */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">EMAIL</label>
                  <div className="col-sm-4">
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={saving}
                    />
                    {errors.email && <span className="text-danger">{errors.email}</span>}
                  </div>
                  <label className="col-sm-2 col-form-label">TELÉFONO</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div className="form-group">
                  <label className="col-sm-2 col-form-label">CELULAR</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.mobile || ''}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="form-group">
                  <div className="offset-sm-2 col-sm-10">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <Spinner size="sm" text="Guardando..." />
                      ) : (
                        <>
                          <i className="fa fa-save"></i> Guardar
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowResetConfirm(true)}
                      disabled={saving}
                      style={{ marginLeft: '10px' }}
                    >
                      <i className="fa fa-refresh"></i> Limpiar
                    </button>
                    <Link
                      to="/patients"
                      className="btn btn-outline-secondary"
                      style={{ marginLeft: '10px' }}
                    >
                      Cancelar
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showResetConfirm}
        title="Limpiar formulario"
        body="Se perderán todos los cambios no guardados en este formulario. ¿Desea continuar?"
        confirmLabel="Limpiar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={false}
        onConfirm={() => { resetForm(); setShowResetConfirm(false); }}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
};

export default PatientEditPage;
